import { Loader2 } from "lucide-react";
import { Topbar } from "@/components/layout/Shell";
import { media } from "@/lib/api";

export const Page = ({ crumbs, children, wide }) => (
  <div className="flex-1 min-w-0 flex flex-col">
    <Topbar crumbs={crumbs} />
    <main className={`flex-1 px-8 py-6 ${wide ? "" : "max-w-[1600px]"} w-full fade-up`}>{children}</main>
  </div>
);

export const Header = ({ title, sub, eyebrow, children }) => (
  <div className="flex items-start justify-between gap-6 mb-6">
    <div>
      {eyebrow && <div className="text-xs tracking-[0.14em] uppercase text-[#5f6f84] mb-1">{eyebrow}</div>}
      <h1 className="font-serif-d text-4xl lg:text-[42px] font-bold text-navy leading-tight tracking-[-0.01em]" data-testid="page-title">{title}</h1>
      {sub && <p className="text-[16px] text-[#5f6f84] mt-1.5">{sub}</p>}
    </div>
    <div className="flex items-center gap-3 shrink-0">{children}</div>
  </div>
);

export const Card = ({ title, action, children, className = "", bodyClass = "p-5", testid }) => (
  <section className={`card-z ${className}`} data-testid={testid}>
    {title && (
      <div className="flex items-center justify-between px-5 pt-4 pb-2">
        <h2 className="font-serif-d text-[19px] font-semibold text-navy">{title}</h2>
        {action}
      </div>
    )}
    <div className={bodyClass}>{children}</div>
  </section>
);

const BADGE = {
  "Düzenleniyor": "bg-[#e3f1f5] text-teal", "Onay bekliyor": "bg-[#fdeceb] text-[#d1453b]", "Üretimde": "bg-[#e6f4ea] text-[#2f8a4c]",
  "Hazır": "bg-[#e6f4ea] text-[#2f8a4c]", "Sırada": "bg-[#fdeceb] text-[#d1453b]", "Üretiliyor": "bg-[#e7f0fd] text-[#3a6fd8]",
  "Başarısız": "bg-[#fdeceb] text-[#d1453b]", "Taslak": "bg-[#eef0f3] text-[#5f6f84]", "Onaylandı": "bg-[#e6f4ea] text-[#2f8a4c]",
  "Tamamlandı": "bg-[#e6f4ea] text-[#2f8a4c]", "Devam ediyor": "bg-[#e3f1f5] text-teal", "Beklemede": "bg-[#eef0f3] text-[#5f6f84]",
  "Başlanmadı": "bg-[#fdeceb] text-[#d1453b]", "Başarılı": "bg-[#e6f4ea] text-[#2f8a4c]", "Sağlıklı": "bg-[#e6f4ea] text-[#2f8a4c]",
  "Bekliyor": "bg-[#fff4e0] text-[#b7791f]", "Kabul edildi": "bg-[#e6f4ea] text-[#2f8a4c]", "Reddedildi": "bg-[#fdeceb] text-[#d1453b]",
  "Eskimiş": "bg-[#fdeceb] text-[#d1453b]", "Sahne üretildi": "bg-[#e6f4ea] text-[#2f8a4c]", "Hata": "bg-[#fdeceb] text-[#d1453b]",
};
export const Badge = ({ s, children, className = "" }) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[12px] font-medium ${BADGE[s] || "bg-[#eef0f3] text-[#5f6f84]"} ${className}`}>{children || s}</span>
);

export const Progress = ({ v, className = "" }) => (
  <div className={`h-1.5 bg-[#e8ecf0] rounded-full overflow-hidden ${className}`}><div className="h-full bg-teal rounded-full transition-[width] duration-500" style={{ width: `${Math.min(100, v || 0)}%` }} /></div>
);

export const Img = ({ src, className = "", alt = "" }) =>
  src ? <img src={media(src)} alt={alt} className={`object-cover ${className}`} loading="lazy" /> : <div className={`bg-[#eef1f4] grid place-items-center text-[#a3aebb] text-xs ${className}`}>Görsel yok</div>;

export const Spin = ({ size = 16 }) => <Loader2 size={size} className="animate-spin" />;

export const Field = ({ label, children, hint }) => (
  <label className="block"><span className="lbl">{label}</span>{children}{hint && <span className="text-[11px] text-[#8a96a8] mt-1 block">{hint}</span>}</label>
);

export const Sel = ({ value, onChange, options, testid, className = "" }) => (
  <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={`inp ${className}`} data-testid={testid}>
    {options.map((o) => (typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>))}
  </select>
);

export const Empty = ({ text, children }) => (
  <div className="card-z p-10 text-center text-[#5f6f84]"><p className="text-[16px] mb-3">{text}</p>{children}</div>
);

export const JobStatus = ({ job }) => job ? (
  <div className="text-xs flex items-center gap-2 mt-2" data-testid="job-status">
    {!["Hazır", "Başarısız"].includes(job.status) && <Spin size={12} />}<Badge s={job.status} /> {job.error && <span className="text-red-600">{job.error}</span>}
    {job.status === "Üretiliyor" && <span className="text-[#8a96a8]">%{job.progress}</span>}
  </div>
) : null;
